import React from "react";

import {
  Alert,
  Box,
  Paper,
  SelectChangeEvent,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

import { FilterBox, HelpText } from "@/components/atoms";
import { SelectableMenuCard } from "@/components/molecules";
import { useAudienceContext } from "@/context";
import { AudienceTravelTime, UploadAudience } from "@/types";
import {
  defaultTravelTime,
  travelTimeDepartureDayOptions,
  travelTimeDepartureTimeOptions,
  travelTimeModeOptions,
  travelTimeTransportOptions,
} from "@/utils";


interface TravelTimeModeProps {
  audience: UploadAudience
  readOnly?: boolean;
}

export const TravelTimeMode: React.FC<TravelTimeModeProps> = ({ audience, readOnly }) => {

  const theme = useTheme();
  const { dispatch } = useAudienceContext();

  const handleChangeMode = (mode: string) => {
    dispatch({
      type: "CHANGE_TRAVELTIME",
      payload: {...(audience.travel_time ?? defaultTravelTime), mode: mode}
    });
  }

  const handleChangeDuration = (event: Event, value: number | number[]) => {
    if (typeof(value) === "number") {
      dispatch({
        type: "CHANGE_TRAVELTIME",
        payload: { ...(audience.travel_time ?? defaultTravelTime), duration: value },
      });
    }
  }

  const handleParamSelect =
    (field: keyof AudienceTravelTime) =>
      (
        event:
          | React.ChangeEvent<HTMLInputElement | { value: unknown }>
          | SelectChangeEvent<string>,
      ) => {
        dispatch({
          type: "CHANGE_TRAVELTIME",
          payload: { ...(audience.travel_time ?? defaultTravelTime), [field]: event.target.value as string },
        });
      };

  return (
    audience.travel_time ? (
      <>
        <Grid size={12}>
          <Alert variant="outlined" severity="info">
            This feature is currently in beta testing and may have limitations or bugs. Please report any issues you encounter.
          </Alert>
        </Grid>
        <Grid size={12}>
          <HelpText
            text="Traveltime Mode"
            helpText="Decide how to combine the traveltime catchments"
            variant="body1"
          />
        </Grid>
        <Grid container size={12} spacing={2}>
          {travelTimeModeOptions.map((option, index) => (
            <Grid key={index} pb={2}>
              <SelectableMenuCard
                title={option.label}
                text={option.description}
                onClick={() => handleChangeMode(option.value)}
                icon={option.icon}
                disabled={readOnly}
                selected={(audience.travel_time && audience.travel_time.mode === option.value) ?? false}
                height="152px"
              />
            </Grid>
          ))}
        </Grid>

        <Grid size={12}>
          <HelpText
            text="Traveltime Parameters"
            helpText="Define the parameters from which to create the traveltime from your uploaded locations"
            variant="body1"
          />
        </Grid>
        <Grid container size={12} spacing={2} pb={2}>
          <Grid>
            <Stack spacing={1}>
              <Typography variant="caption">
                Transportation
              </Typography>
              <FilterBox
                label="Transportation"
                value={audience.travel_time.transportation}
                options={travelTimeTransportOptions}
                onChange={handleParamSelect('transportation')}
                showSelected
                width={236}
                color="secondary"
                disabled={readOnly}
              />
            </Stack>
          </Grid>
          <Grid>
            <Stack
              spacing={1}
              width={236}
              justifyContent="center"
              height="100%"
            >
              <Typography variant="caption">
                Duration
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                px={1}
                height={41}
                sx={{
                  border: "1px solid",
                  borderColor: readOnly ? theme.palette.divider : theme.palette.secondary.main,
                  borderRadius: "4px"
                }}
              >
                <AccessTime fontSize="small" color={readOnly ? "disabled" : "inherit"}/>
                <Slider
                  value={audience.travel_time.duration}
                  onChange={handleChangeDuration}
                  disabled={readOnly}
                  defaultValue={15}
                  step={15}
                  min={15}
                  max={60}
                  marks
                  color="secondary"
                  size="small"
                  sx={{
                    width: 120
                  }}
                  // valueLabelDisplay="off"
                  // getAriaValueText={(value: number) => `${(value / 60)} minutes`}
                />
                <Typography variant="body2" color={readOnly ? "textSecondary" : "secondary"}>
                  {`${audience.travel_time.duration} mins`}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid>
            <Stack spacing={1}>
              <Typography variant="caption">
                Departure Day
              </Typography>
              <FilterBox
                label="Departure Day"
                value={audience.travel_time.departure_day}
                options={travelTimeDepartureDayOptions}
                onChange={handleParamSelect('departure_day')}
                showSelected
                width={236}
                color="secondary"
                disabled={readOnly}
              />
            </Stack>
          </Grid>
          <Grid>
            <Stack spacing={1}>
              <Typography variant="caption">
                Departure Time
              </Typography>
              <FilterBox
                label="Departure Time"
                value={audience.travel_time.departure_time}
                options={travelTimeDepartureTimeOptions}
                onChange={handleParamSelect('departure_time')}
                showSelected
                width={236}
                color="secondary"
                disabled={readOnly}
              />
            </Stack>
          </Grid>
        </Grid>

        <Grid size={12}>
          <HelpText
            text="Select File"
            helpText="Select the file you wish to upload, making sure that it adheres to the specific upload format"
            variant="body1"
          />
        </Grid>
        {!readOnly && (
          <Grid size={12}>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography>
                Upload a CSV file containing the postcode of your locations along with a name.
              </Typography>
              <Typography gutterBottom>
                An example file format is as follows
              </Typography>
              <Box sx={{ pt: 2 }}>
                <TableContainer component={Paper} sx={{ width: 240 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{}}>
                        <TableCell
                          sx={{
                            fontFamily: "cera-pro-bold",
                            borderBottomColor: theme.palette.divider,
                          }}
                        >
                          Postcode
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "cera-pro-bold",
                            borderBottomColor: theme.palette.divider,
                          }}
                        >
                          Name
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ borderBottomColor: theme.palette.divider }}>
                          ABC 123
                        </TableCell>
                        <TableCell sx={{ borderBottomColor: theme.palette.divider }}>
                          Site 1
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>ABC 456</TableCell>
                        <TableCell>Site 2</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          </Grid>
        )}
      </>
    ) : null
  );
};

export default TravelTimeMode;
